import NiceModal from "@ebay/nice-modal-react";
import Box from "@mui/material/Box";
import { type Allocation, type Fund } from "@repo/api";
import { StepperType } from "@repo/logic";
import { cs, PensionQuestionnaireStepperButton, useIsDesktop, useTypedTranslation } from "@repo/ui";
import { type FC, useEffect, useState } from "react";
import { useContractId } from "../../../context/ContractIDContext";
import { useContractById, usePhoneNumbers } from "../../../queryHooks";
import Attention from "../../AttentionBlock";
import { useSmartAction } from "../../Contract/SmartAction/context";
import { useContractTypeContext } from "../../../context/ContractTypeContext";

export type DistributionProps = {
    isFixed?: boolean;
    allocation?:
        | Allocation[]
        | {
              fundCode: undefined;
          }[];
};

export const fundsDistributionDefault = [
    {
        fundCode: undefined,
    },
    {
        fundCode: undefined,
    },
    {
        fundCode: undefined,
    },
];

const ChangeSavingsStrategy: FC<{
    title: string;
    refetch: () => void;
    funds: Fund[];
    isFundsChangeInProgress: boolean;
    isLoading: boolean;
}> = ({ title, refetch, isFundsChangeInProgress, isLoading }) => {
    const { t } = useTypedTranslation();
    const [drawerIsOpen, setDrawerIsOpen] = useState(false);
    const isDesktop = useIsDesktop();
    const phoneNumbers = usePhoneNumbers();
    const { contractId: id } = useContractId();
    const { contractType } = useContractTypeContext();
    const { data: contract } = useContractById({
        id,
    });
    const isChildrenContract = Boolean(contract?.isChildrenContract);
    const authPhoneNumbers = !phoneNumbers.isLoading && phoneNumbers.data;

    const { action, clearAction } = useSmartAction();
    useEffect(() => {
        // show dialog after data is loaded
        if (
            (action?.type === "InvestmentStrategy" || action?.type === "InvestmentStrategyBankId") &&
            !isLoading &&
            !drawerIsOpen
        ) {
            clearAction();
            setDrawerIsOpen(true);
            NiceModal.show("Nicemodal-PensionQuestionnaireStepperModal", {
                additionalContentProps: {
                    contractId: id,
                    contractType,
                    type: "detail",
                    name: StepperType.ChangeInvestmentStrategy,
                    title: t("investmentStrategy.changeSavingsStrategy"),
                },
            }).then(() => {
                setDrawerIsOpen(false);
                refetch();
            });
        }
    }, [action, clearAction, drawerIsOpen, id, contractType, isLoading, refetch, t]);

    return (
        <>
            {authPhoneNumbers && authPhoneNumbers.length === 0 && (
                <Attention severity="info" title={cs.investmentStrategy.alerts.noPhone.title} sx={{ mb: 4 }}>
                    <span
                        dangerouslySetInnerHTML={{
                            __html: cs.investmentStrategy.alerts.noPhone.description,
                        }}
                    />
                </Attention>
            )}
            <Box mt={isDesktop ? 4 : 0}>
                {isFundsChangeInProgress && (
                    <Attention severity="info" sx={{ mb: 4 }}>
                        {cs.investmentStrategy.alerts.fundChangeInProgress.description}
                    </Attention>
                )}
                <PensionQuestionnaireStepperButton
                    type="detail"
                    buttonProps={{
                        disabled: !authPhoneNumbers || isFundsChangeInProgress || isLoading,
                        title,
                    }}
                    contractId={id}
                    contractType={contractType}
                />
            </Box>
        </>
    );
};

export default ChangeSavingsStrategy;
